import React, { FC, useState, useEffect, useRef } from 'react';
import { find, findIndex, cloneDeep } from 'lodash';
import { Formik, Form, FastField, FormikProps } from 'formik';
import * as Yup from 'yup';
import { Button, Card, Container, Row } from 'react-bootstrap';
import ModalComponent from '../../components/modal/ModalComponent';
import { useQuery } from '../../hooks/useQuery';
import useCompleteConnect from '../../hooks/api/payment/stripe/useCompleteConnect';
import usePaymentAccounts from '../../hooks/api/payment/stripe/usePaymentAccounts';
import FieldWrapper from '../../components/library/inputs/fieldWrapper/FieldWrapper';
import TextInput from '../../components/library/inputs/text/TextInput';
import { PaymentAccount } from '../../services/payment/PaymentService';
import PageTitle from '../../components/layout/pageTitle/PageTitle';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { addErrorToast, addSuccessToast } from '../../components/errors/toast/ToastUtils';
import EmptyList from '../../components/layout/emptyList/EmptyList';
import LoadingSpinner from '../../components/layout/loadingSpinner/LoadingSpinner';
import { getSecuritySection, SecuritySections, SecurityModel } from '../../models/security/security';
import { useHistory } from 'react-router';
import { FallbackPage } from '../../components/layout/navbar/Navbar';

const types = {
  stripe: {
    id: 'stripe',
    name: 'Stripe',
    logo: 'https://cache.kreezee.com/images/stripe-logo.png',
  },
  'sportspay-ca': {
    id: 'sportspay-ca',
    name: 'SportsPay (Canada)',
    logo: 'https://94998824c1.imgdist.com/pub/bfra/6o27w4hz/v7f/ttu/wqr/SportsPayCMYK_24.png', // Changed logo URL to avoid duplication
  },
  'sportspay-us': {
    id: 'sportspay-us',
    name: 'SportsPay (United States)',
    logo: 'https://live.staticflickr.com/65535/54386315404_091f526f15_n.jpg', // Changed logo URL to avoid duplication
  },
};

interface Values {
  name: string;
  type: string;
}

const PaymentAccountsPage: FC = () => {
  const [showAddAccount, setShowAddAccount] = useState<boolean>(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<boolean>(false);
  const [selectedAccount, setSelectedAccount] = useState<string | null>(null);
  const [paymentSecurity, setPaymentSecurity] = useState<SecurityModel | null>(null);
  const { completeConnect, revokeConnect } = useCompleteConnect();
  const {
    paymentAccounts,
    setPaymentAccounts,
    setOrganizationId,
    updatePaymentAccount,
    reloadPaymentAccounts,
    isLoading,
  } = usePaymentAccounts();
  const security = useSelector((state: RootState) => state.organization.organization?.security);
  const organizationId = useSelector((state: RootState) => state.organization.organization?.id);
  const formRef = useRef<FormikProps<Values>>(null);
  const intl = useIntl();
  const history = useHistory();
  const query = useQuery();
  const code = query.get('code');
  const state = query.get('state');
  const data = query.get('data');
  const status = query.get('status') || '';

  useEffect(() => {
    if (organizationId) {
      if (code) {
        let name;
        let type;
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const _data: string | null = state || data;
        if (_data) {
          const _state = Buffer.from(_data, 'base64').toString('binary');
          ({ name, type } = JSON.parse(_state));
        }
        completeConnect(organizationId, code, name, type, status).then(() => {
          setOrganizationId(organizationId);
        });
      } else {
        setOrganizationId(organizationId);
      }
    }
  }, [code, organizationId, data, status]);

  useEffect(() => {
    if (security) {
      setPaymentSecurity(getSecuritySection(security, SecuritySections.PAYMENT_ACCOUNTS));
    }
    if (paymentSecurity && !paymentSecurity?.read) {
      history.push(FallbackPage);
    }
  }, [security, paymentSecurity]);

  const connectAccount = (values: Values) => {
    const state = Buffer.from(JSON.stringify(values), 'binary').toString('base64');

    if (values.type === 'sportspay-ca') {
      window.location.href = `${global.window.configs.sp_CA_account_Url}?Data=${state}&OrgID=${organizationId}&CallbackUrl=${global.window.configs.uri}/payment-accounts`;
    } else if (values.type === 'sportspay-us') {
      window.location.href = `${global.window.configs.sp_US_account_Url}?Data=${state}&OrgID=${organizationId}&CallbackUrl=${global.window.configs.uri}/payment-accounts`;
    } else {
      window.location.href = `https://connect.stripe.com/oauth/authorize?client_id=${global.window.configs.stripeClientId}&state=${state}&response_type=code&scope=read_write&redirect_uri=${global.window.configs.uri}/payment-accounts`;
    }
  };

  const updateAccount = (values: Values) => {
    if (paymentAccounts && selectedAccount) {
      const accounts = cloneDeep(paymentAccounts);
      const accountIndex = findIndex(accounts, { stripe_user_id: selectedAccount });
      const account = accounts[accountIndex] as PaymentAccount;
      const paymentAccount = { ...account, account_name: values.name, type: values.type };

      updatePaymentAccount(organizationId as string, paymentAccount)
        .then((updateAccount) => {
          accounts[accountIndex] = updateAccount;
          setPaymentAccounts(accounts);
          addSuccessToast({ message: intl.formatMessage({ id: 'paymentAccounts.labels.editSuccess' }) });
        })
        .catch(() => {
          addErrorToast({ message: intl.formatMessage({ id: 'paymentAccounts.labels.editError' }) });
        });

      setShowAddAccount(false);
    }
  };

  const addAccount = () => {
    setSelectedAccount(null);
    setShowAddAccount(true);
  };

  const renderPaymentAccount = (account, i) => {
    return (
      <div
        className="payment-account d-flex align-items-stretch justify-content-between mb-2"
        key={`payment-account-${i}`}
      >
        <div className="account-details d-flex align-items-center">
          <div>
            <img src={types[account.type].logo} />
          </div>
          <div className="ms-3">
            <div className="fw-bold title">
              {account.account_name || types[account.type].name}
              {account.type === 'sportspay-us' ||
              (account.type === 'sportspay-ca' && !account.stripe_publishable_key) ? (
                <div>
                  <span className="badge badge-warning">{account.status}</span>
                </div>
              ) : (
                ''
              )}
            </div>
            <div className="account-infos">{account.stripe_user_id}</div>
            <div className="account-infos">{account.creationDate}</div>
          </div>
        </div>
        <div className="d-flex align-items-center justify-content-center buttons-wrapper">
          {!(account.type.includes('sportspay') && account.status === 'in-review') && (
            <Button
              variant="primary"
              className="me-1 d-flex align-items-center justify-content-center"
              onClick={() => {
                const accountType = account.type;
                let url = '';
                if (accountType === 'stripe') {
                  url = 'https://dashboard.stripe.com/login';
                } else if (accountType === 'sportspay-ca') {
                  if (account.status === 'created') {
                    url = `${global.window.configs.sp_Host}/en-ca`;
                  } else {
                    url = `${global.window.configs.sp_CA_account_Url}?OrgID=${organizationId}&CallbackUrl=${global.window.configs.uri}/payment-accounts`;
                  }
                } else if (accountType === 'sportspay-us') {
                  if (account.status === 'created') {
                    url = `${global.window.configs.sp_Host}/en-us`;
                  } else {
                    url = `${global.window.configs.sp_US_account_Url}?OrgID=${organizationId}&CallbackUrl=${global.window.configs.uri}/payment-accounts`;
                  }
                }
                window.open(url, '_blank');
              }}
            >
              <span className="mdi mdi-open-in-new pe-1"></span>
              <span>
                {account.type === 'stripe'
                  ? 'Go to Stripe'
                  : account.stripe_publishable_key
                  ? 'Go to SportsPay'
                  : 'Go To Application'}
              </span>
            </Button>
          )}

          <Button
            className="edit-btn d-flex align-items-center justify-content-center"
            onClick={() => {
              setSelectedAccount(account.stripe_user_id);
              setShowAddAccount(true);
            }}
            disabled={!paymentSecurity?.update}
          >
            <span className="mdi mdi-pencil"></span>
          </Button>
          <Button
            className="delete-btn d-flex align-items-center justify-content-center"
            onClick={() => {
              setSelectedAccount(account.stripe_user_id);
              setShowDeleteConfirm(true);
            }}
            disabled={!paymentSecurity?.delete || account.type !== 'stripe'}
          >
            <span className="mdi mdi-trash-can-outline"></span>
          </Button>
        </div>
      </div>
    );
  };

  const renderModal = () => {
    const account = find(paymentAccounts, { stripe_user_id: selectedAccount }) as PaymentAccount;
    let initialName = '';
    let action = connectAccount;
    let edit = false;
    const spAccounts =
      paymentAccounts?.filter((account) => account.type === 'sportspay-ca' || account.type === 'sportspay-us').length ||
      0;

    let initialType = spAccounts > 0 ? 'stripe' : 'sportspay-ca';

    if (account) {
      edit = true;
      initialName = account.account_name;
      initialType = account.type;
      action = updateAccount;
    }

    return (
      <ModalComponent
        modalHeading={intl.formatMessage({ id: 'paymentAccounts.labels.editModalTitle' })}
        modalSize={'lg'}
        show={showAddAccount}
        buttons={[
          {
            text: intl.formatMessage({ id: 'paymentAccounts.buttons.cancel' }),
            buttonVariant: 'secondary',
            onClick: () => setShowAddAccount(false),
          },
          {
            text: intl.formatMessage({ id: 'paymentAccounts.buttons.save' }),
            buttonVariant: 'primary',
            onClick: () => formRef.current?.submitForm(),
          },
        ]}
        closeSettings={() => setShowAddAccount(false)}
      >
        <Formik
          initialValues={{
            name: initialName,
            type: initialType,
          }}
          validationSchema={Yup.object().shape({
            name: Yup.string(),
            type: Yup.string(),
          })}
          onSubmit={action}
          innerRef={formRef}
        >
          {({ handleSubmit }) => (
            <Form className="edit-modal-form" noValidate onSubmit={handleSubmit}>
              <FastField name={'type'}>
                {({ meta, field }) => {
                  return (
                    <div className="d-flex align-items-center justify-content-center">
                      <FieldWrapper
                        error={meta.error}
                        touched={meta.touched ? meta.touched.toString() : undefined}
                        hiddenText={false}
                        textColor={undefined}
                        required={false}
                        label="Payment Method"
                        visible={!edit}
                        size={12}
                      >
                        <select
                          id="paymentMethod"
                          name="type"
                          className="form-control"
                          onChange={field.onChange}
                          onBlur={field.onBlur}
                          value={field.value}
                        >
                          {spAccounts == 0 && (
                            <>
                              <option value="sportspay-ca">SportsPay (Canada)</option>
                              <option value="sportspay-us">SportsPay (United States)</option>
                            </>
                          )}
                          <option value="stripe">Stripe</option>
                        </select>
                      </FieldWrapper>
                    </div>
                  );
                }}
              </FastField>

              <FastField name={'name'}>
                {({ meta, field }) => {
                  return (
                    <div className="d-flex align-items-center justify-content-center">
                      <FieldWrapper
                        error={meta.error}
                        touched={meta.touched}
                        hiddenText={false}
                        textColor={undefined}
                        required={false}
                        label={intl.formatMessage({ id: 'paymentAccounts.labels.accountName' })}
                        visible={true}
                        size={12}
                      >
                        <TextInput
                          id="name"
                          onChange={field.onChange}
                          onBlur={field.onBlur}
                          enabled={true}
                          placeholder={'Name'}
                          value={field.value}
                          touched={meta.touched}
                          error={meta.error}
                        />
                      </FieldWrapper>
                    </div>
                  );
                }}
              </FastField>
            </Form>
          )}
        </Formik>
      </ModalComponent>
    );
  };

  return (
    <React.Fragment>
      <div className="page-content payment-accounts">
        <Container fluid>
          <PageTitle title={intl.formatMessage({ id: 'navbar.settings.paymentOptions.title' })}></PageTitle>
          <Row>
            <Card className="col-xl-12">
              {isLoading && <LoadingSpinner />}
              {!isLoading && (
                <Card.Body>
                  {paymentAccounts && paymentAccounts.length > 0 ? (
                    <div>{paymentAccounts.map((account, i) => renderPaymentAccount(account, i))}</div>
                  ) : (
                    <EmptyList message={intl.formatMessage({ id: 'paymentAccounts.labels.noAccounts' })} />
                  )}
                  <div className={paymentAccounts && paymentAccounts.length > 0 ? '' : 'noAccounts'}>
                    <Button className="add-button" onClick={() => addAccount()} disabled={!paymentSecurity?.write}>
                      <span className="mdi mdi-plus"></span>
                      <FormattedMessage id="paymentAccounts.buttons.addAccount"></FormattedMessage>
                    </Button>
                  </div>
                </Card.Body>
              )}
            </Card>
          </Row>
        </Container>
        {renderModal()}
        <ModalComponent
          modalHeading={intl.formatMessage({ id: 'paymentAccounts.labels.delete' })}
          modalSize={'lg'}
          show={showDeleteConfirm}
          buttons={[
            {
              text: intl.formatMessage({ id: 'paymentAccounts.buttons.cancel' }),
              buttonVariant: 'secondary',
              onClick: () => setShowDeleteConfirm(false),
            },
            {
              text: intl.formatMessage({ id: 'paymentAccounts.buttons.delete' }),
              buttonVariant: 'primary',
              onClick: () => {
                if (selectedAccount) {
                  revokeConnect(organizationId as string, selectedAccount)
                    .then(() => {
                      reloadPaymentAccounts();
                      addSuccessToast({ message: intl.formatMessage({ id: 'paymentAccounts.labels.deleteSuccess' }) });
                      setShowDeleteConfirm(false);
                    })
                    .catch(() => {
                      addErrorToast({ message: intl.formatMessage({ id: 'paymentAccounts.labels.deleteError' }) });
                    });
                }
              },
            },
          ]}
          closeSettings={() => setShowDeleteConfirm(false)}
        >
          <FormattedMessage id="paymentAccounts.labels.deleteAccount"></FormattedMessage>
        </ModalComponent>
      </div>
    </React.Fragment>
  );
};

export default PaymentAccountsPage;
